import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Paperclip, Send, X } from 'lucide-react';
import SnippetsMenu from './SnippetsMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.pdf,.docx,.xlsx,.jpg,.jpeg,.png,.mp3,.mp4';

const ReplyComposer = ({ onSend, sending, canPickSender = true }) => {
  const [body, setBody] = useState('');
  const [identities, setIdentities] = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [files, setFiles] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/email-identities`, { withCredentials: true })
      .then((res) => {
        const list = res.data.identities || [];
        setIdentities(list);
        if (list.length) setFromEmail(list[0].email);
      })
      .catch(() => {});
  }, []);

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleInsertSnippet = (snippetBody) => {
    setBody((prev) => (prev.trim() ? `${prev}\n\n${snippetBody}` : snippetBody));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending || !fromEmail) return;
    const ok = await onSend(body.trim(), fromEmail, files);
    if (ok) {
      setBody('');
      setFiles([]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t border-slate-200 p-4 space-y-3" data-testid="lead-reply-composer">
      <div className="flex items-center gap-2 text-sm">
        <label className="text-slate-500 font-medium">From:</label>
        {canPickSender ? (
          <select
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            data-testid="lead-reply-from-select"
            className="border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            {identities.map((idn) => (
              <option key={idn.id} value={idn.email}>{idn.label} — {idn.email}</option>
            ))}
          </select>
        ) : (
          <span className="text-slate-600 font-medium" data-testid="lead-reply-from-fixed">
            {identities.find((i) => i.email === fromEmail)?.label || 'English Passion Team'} — {fromEmail}
          </span>
        )}
      </div>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply…"
        rows={3}
        data-testid="lead-reply-body-input"
        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm resize-none"
      />

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="lead-reply-attachment-chips">
          {files.map((f, idx) => (
            <span key={idx} className="flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full">
              <Paperclip className="w-3 h-3" /> {f.name}
              <button type="button" onClick={() => removeFile(idx)} data-testid={`lead-reply-remove-attachment-${idx}`}>
                <X className="w-3 h-3 hover:text-red-500" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 cursor-pointer">
            <Paperclip className="w-4 h-4" /> Attach files
            <input type="file" multiple accept={ACCEPT} onChange={handleFileSelect} className="hidden" data-testid="lead-reply-attachment-input" />
          </label>
          <SnippetsMenu onInsert={handleInsertSnippet} />
        </div>
        <button
          type="submit"
          disabled={!body.trim() || sending || !fromEmail}
          data-testid="lead-reply-send-button"
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  );
};

export default ReplyComposer;
